// src/components/layout/ChoixDeclaration.tsx — SCE Issues
// Le bouton + jaune (et « Déclarer » de l'accueil) ouvre un choix (06/10) :
//   - Un problème → /new, comme avant ;
//   - Une NC-S → on choisit d'abord LE PROBLÈME (un existant, ou « Nouveau
//     problème ») : une NC-S est toujours rattachée à un problème (photos,
//     contexte, suivi, badge, N° retrouvé — décision de Guillaume). Puis la
//     fiche s'ouvre sur le formulaire NC-S, pré-rempli.
//   - Une FNC-T (15/10, administrateur pendant la construction) → on choisit
//     d'abord LA MACHINE (« machine d'abord », Guillaume) ; sa fiche s'ouvre sur
//     la FNC-T (ChoixMachineFnct, FnctDepuisMachine).
//
// ─── Le mouvement (styles `.declarer-*` de globals.css) ───
//
// Depuis le + (barre du bas) : le rond SE DIVISE (Guillaume, 06/10) — il
// pivote en × (en restant jaune), et deux ronds jaunes en sortent, s'écartent et montent, chacun
// avec son libellé ; ils s'y rejoignent à la fermeture. Avec la FNC-T (15/10) : TROIS
// ronds en triangle — le problème en haut, la NC-S à gauche, la FNC-T à droite. Depuis « Déclarer »
// de l'accueil (pas un rond) : une fenêtre centrée au bureau, des cartes au
// téléphone. Le choix du problème glisse du bas (téléphone) ou s'ouvre au
// centre (bureau). Tout s'arrête avec « réduire les animations ».
//
// Depuis la barre du bas (`dansBarre`), tout est rendu DANS la barre, que
// BottomNav monte au-dessus de l'en-tête le temps du choix : le + reste
// visible au-dessus du voile, et sert à refermer. Ailleurs (accueil), le
// choix est rendu à la racine de la page.
//
// Un fournisseur n'a qu'une option (pas de NC-S contre un fournisseur) : le
// bouton mène alors directement à /new.

import { useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, ChevronRight, ClipboardList, FileSpreadsheet, Plus, Search, X } from 'lucide-react'
import { EtatProbleme } from '@/components/ui/EtatProbleme'
import { useClicFond } from '@/hooks/useClicFond'
import { PastilleNcs } from '@/components/issues/PastilleNcs'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useIssues } from '@/hooks/useIssues'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { lienEquipement, lienProbleme } from '@/utils/liensFiches'
import { accesFnct } from '@/utils/droitsFnct'
import { FNCT_MASQUEE } from '@/config/fnct.config'
import { ChoixMachineFnct } from '@/components/fnct/ChoixMachineFnct'
import { identitesDeclarant } from '@/utils/identiteDeclarant'
import { ageCourt, sectionsPourNcs } from '@/utils/choixProblemeNcs'
import type { EtatModale } from '@/components/layout/ModaleRoute'
import type { Issue } from '@/types/issue.types'

/** État de navigation : la page de déclaration enchaîne ensuite sur la NC-S. */
export interface EtatDeclarationNcs {
  ncs?: boolean
}

export interface UseChoixDeclarationReturn {
  /** Ouvert (le + est un ×). Faux dès que la fermeture commence. */
  ouvert: boolean
  /** Affiché, fermeture comprise : la barre reste au-dessus jusqu'au bout. */
  affiche: boolean
  /** Au clic sur « + » / « Déclarer » : ouvre, ou referme si c'est ouvert. */
  basculer: () => void
  /** Le choix, à rendre par l'appelant (null si fermé). */
  feuille: ReactNode
}

/**
 * @param dansBarre rendu dans la barre du bas (le + reste au-dessus du voile).
 */
export function useChoixDeclaration({ dansBarre = false }: { dansBarre?: boolean } = {}): UseChoixDeclarationReturn {
  const navigate = useNavigate()
  const { isFournisseur } = useCurrentUser()
  const [ouvert, setOuvert] = useState(false)
  // Les ronds rejoignent le + avant de disparaître (~0,35 s).
  const [fermeture, setFermeture] = useState(false)
  const minuterie = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(minuterie.current), [])

  const fermer = (anime: boolean): void => {
    window.clearTimeout(minuterie.current)
    const calme = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!anime || !dansBarre || calme) {
      setFermeture(false)
      setOuvert(false)
      return
    }
    setFermeture(true)
    minuterie.current = window.setTimeout(() => {
      setFermeture(false)
      setOuvert(false)
    }, DUREE_FERMETURE_MS)
  }

  const basculer = (): void => {
    if (isFournisseur) navigate('/new')
    else if (ouvert) fermer(true)
    else setOuvert(true)
  }
  const choix = ouvert ? (
    <ChoixDeclaration
      onClose={() => fermer(true)}
      onQuitter={() => fermer(false)}
      dansBarre={dansBarre}
      fermeture={fermeture}
    />
  ) : null
  return {
    ouvert: ouvert && !fermeture,
    affiche: ouvert,
    basculer,
    feuille: choix && (dansBarre ? choix : createPortal(choix, document.body)),
  }
}

/** Durée du retour des ronds dans le + (`declarer-rejoint` de globals.css). */
const DUREE_FERMETURE_MS = 350

function ChoixDeclaration({
  onClose,
  onQuitter,
  dansBarre,
  fermeture,
}: {
  /** Fermer en refermant le mouvement (×, voile, Échap). */
  onClose: () => void
  /** Fermer tout de suite (on part vers une page). */
  onQuitter: () => void
  dansBarre: boolean
  fermeture: boolean
}): JSX.Element {
  const navigate = useNavigate()
  const estBureau = useEstBureau()
  const [etape, setEtape] = useState<'choix' | 'probleme' | 'machine'>('choix')
  const { user } = useCurrentUser()
  // La FNC-T : administrateur et testeurs pendant la construction (comme partout ailleurs).
  const avecFnct = !FNCT_MASQUEE && accesFnct(user?.upn)
  const gestesFond = useClicFond(onClose)

  // Échap : revenir au choix, puis fermer.
  useEffect(() => {
    function surTouche(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return
      if (etape !== 'choix') setEtape('choix')
      else onClose()
    }
    window.addEventListener('keydown', surTouche)
    return () => window.removeEventListener('keydown', surTouche)
  }, [etape, onClose])

  const voile = (
    <div className="declarer-voile" data-dessus={!dansBarre} data-fermeture={fermeture} {...gestesFond} role="presentation" />
  )

  if (etape === 'probleme') {
    return (
      <>
        {voile}
        <ChoixProbleme onClose={onQuitter} onRetour={() => setEtape('choix')} />
      </>
    )
  }

  if (etape === 'machine') {
    return (
      <>
        {voile}
        <ChoixMachineFnct
          onRetour={() => setEtape('choix')}
          onClose={onQuitter}
          onChoisir={(eq) => {
            onQuitter()
            navigate(lienEquipement(eq.numeroSerie), { state: { ouvrirFnct: true } satisfies EtatModale })
          }}
        />
      </>
    )
  }

  // Depuis le + : le rond se divise en deux ronds (trois en triangle avec la FNC-T), qui s'écartent et montent.
  if (dansBarre) {
    return (
      <>
        {voile}
        <div role="dialog" aria-modal="true" aria-label="Que voulez-vous déclarer ?" data-fermeture={fermeture}>
          <p className="declarer-eclat-titre" data-triangle={avecFnct}>Que voulez-vous déclarer ?</p>
          <Branche
            cote={avecFnct ? 'haut' : 'gauche'}
            icone={<AlertTriangle size={26} />}
            titre="Un problème"
            detail="Un défaut sur un équipement"
            onClick={() => {
              onQuitter()
              navigate('/new')
            }}
          />
          <Branche
            cote={avecFnct ? 'gauche' : 'droite'}
            triangle={avecFnct}
            icone={<FileSpreadsheet size={26} />}
            titre="Une NC-S"
            detail="Un composant fournisseur non conforme"
            onClick={() => setEtape('probleme')}
          />
          {avecFnct && (
            <Branche
              cote="droite"
              triangle
              icone={<ClipboardList size={26} />}
              titre="Une FNC-T"
              detail="Une non-conformité de test"
              onClick={() => setEtape('machine')}
            />
          )}
        </div>
      </>
    )
  }

  const options = [
    <Option
      key="probleme"
      icone={<AlertTriangle size={24} />}
      titre="Un problème"
      detail="Un défaut constaté sur un équipement : en moins d'une minute, avec photos."
      delai={estBureau ? 0.04 : 0.09}
      onClick={() => {
        onQuitter()
        navigate('/new')
      }}
    />,
    <Option
      key="ncs"
      icone={<FileSpreadsheet size={24} />}
      titre="Une NC-S"
      detail="Un composant non conforme à réclamer au fournisseur, envoyé à la qualité."
      delai={estBureau ? 0.09 : 0.03}
      onClick={() => setEtape('probleme')}
    />,
    ...(avecFnct
      ? [
          <Option
            key="fnct"
            icone={<ClipboardList size={24} />}
            titre="Une FNC-T"
            detail="Une non-conformité de test : écart P&ID / PPL relevé, ou ouverte en quatre étapes."
            delai={estBureau ? 0.14 : 0}
            onClick={() => setEtape('machine')}
          />,
        ]
      : []),
  ]

  // Bureau : une fenêtre centrée, les choix côte à côte.
  if (estBureau) {
    return (
      <>
        {voile}
        <div className="declarer-panneau" role="dialog" aria-modal="true" aria-label="Que voulez-vous déclarer ?">
          <div className="declarer-panneau-entete" style={{ padding: '18px 16px 4px 24px' }}>
            <h2 className="declarer-panneau-titre" style={{ fontSize: 22 }}>Que voulez-vous déclarer ?</h2>
            <button type="button" className="declarer-icone-bouton" onClick={onClose} aria-label="Fermer">
              <X size={22} />
            </button>
          </div>
          <div className="declarer-panneau-corps" style={{ padding: '12px 24px 24px' }}>
            <div className="declarer-grille" data-trois={avecFnct}>{options}</div>
          </div>
        </div>
      </>
    )
  }

  // Téléphone : les choix montent du bouton + (le plus proche du bouton
  // apparaît le premier).
  return (
    <>
      {voile}
      <div className="declarer-pile" role="dialog" aria-modal="true" aria-label="Que voulez-vous déclarer ?">
        <p className="declarer-pile-titre">Que voulez-vous déclarer ?</p>
        {[...options].reverse()}
      </div>
    </>
  )
}

/** Un des deux ronds nés du + : le rond, puis son libellé en dessous. */
function Branche({
  cote,
  triangle = false,
  icone,
  titre,
  detail,
  onClick,
}: {
  cote: 'haut' | 'gauche' | 'droite'
  /** Trois ronds en triangle (avec la FNC-T) : les côtés descendent, le haut monte. */
  triangle?: boolean
  icone: ReactNode
  titre: string
  detail: string
  onClick: () => void
}): JSX.Element {
  return (
    <button type="button" className="declarer-branche" data-cote={cote} data-triangle={triangle} onClick={onClick}>
      <span className="declarer-bulle">{icone}</span>
      <span className="declarer-bulle-texte">
        <span className="declarer-bulle-titre">{titre}</span>
        <span className="declarer-bulle-detail">{detail}</span>
      </span>
    </button>
  )
}

function Option({
  icone,
  titre,
  detail,
  delai,
  onClick,
}: {
  icone: ReactNode
  titre: string
  detail: string
  /** Décalage de l'apparition, en secondes. */
  delai: number
  onClick: () => void
}): JSX.Element {
  return (
    <button type="button" className="declarer-option" style={{ animationDelay: `${delai}s` }} onClick={onClick}>
      <span className="declarer-option-icone">{icone}</span>
      <span className="declarer-option-texte">
        <span className="declarer-option-titre">{titre}</span>
        <span className="declarer-option-detail">{detail}</span>
      </span>
    </button>
  )
}

/** Étape 2 : sur quel problème ? (monté seulement ici : la liste n'est lue qu'à ce moment) */
function ChoixProbleme({ onClose, onRetour }: { onClose: () => void; onRetour: () => void }): JSX.Element {
  const navigate = useNavigate()
  const estBureau = useEstBureau()
  const { user } = useCurrentUser()
  const { issues, loading, error } = useIssues()
  const [recherche, setRecherche] = useState('')
  const [maintenant] = useState(() => Date.now())

  const sections = useMemo(() => {
    const moi = new Set((user ? identitesDeclarant(user) : []).map((x) => x.toLowerCase()))
    return sectionsPourNcs(issues, recherche, (i) => moi.has((i.declarant ?? '').toLowerCase()))
  }, [issues, recherche, user])

  const vers = (chemin: string, etat: EtatModale | EtatDeclarationNcs): void => {
    onClose()
    navigate(chemin, { state: etat })
  }

  let rang = 0
  return (
    <div className="declarer-panneau" role="dialog" aria-modal="true" aria-label="Une NC-S, sur quel problème ?">
      <div className="declarer-panneau-entete">
        <button type="button" className="declarer-icone-bouton" onClick={onRetour} aria-label="Retour au choix">
          <ArrowLeft size={22} />
        </button>
        <h2 className="declarer-panneau-titre">Une NC-S, sur quel problème ?</h2>
        <button type="button" className="declarer-icone-bouton" onClick={onClose} aria-label="Fermer">
          <X size={22} />
        </button>
      </div>

      <div className="declarer-panneau-corps">
        <p className="declarer-intro">
          Une NC-S est toujours rattachée à un problème : photos, contexte et suivi restent ensemble.
        </p>

        <button type="button" className="declarer-nouveau" onClick={() => vers('/new', { ncs: true })}>
          <span className="declarer-nouveau-icone">
            <Plus size={22} strokeWidth={2.5} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 16, fontWeight: 600 }}>Nouveau problème</span>
            <span style={{ display: 'block', fontSize: 13.5, lineHeight: '19px' }}>
              L’équipement, le défaut, les photos — puis la NC-S, déjà pré-remplie.
            </span>
          </span>
          <ChevronRight size={20} />
        </button>

        <div className="declarer-separateur">ou un problème déjà déclaré</div>

        <label className="declarer-recherche">
          <Search size={18} color="var(--color-gray-60)" aria-hidden="true" />
          <input
            type="search"
            autoFocus={estBureau}
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="N° de série, P2026-…, OF, PN, titre"
            aria-label="Chercher un problème"
          />
        </label>

        {error && <p className="declarer-vide" style={{ color: 'var(--color-traffic-red)' }}>{error}</p>}
        {loading && issues.length === 0 && <p className="declarer-vide">Chargement des problèmes…</p>}
        {!loading && !error && sections.length === 0 && (
          <p className="declarer-vide">
            {recherche.trim().length >= 2 ? `Aucun problème ne correspond à « ${recherche.trim()} ».` : 'Aucun problème en cours.'}
          </p>
        )}

        {sections.map((s) => (
          <section key={s.titre}>
            <h3 className="declarer-section-titre">{s.titre}</h3>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {s.issues.map((i) => (
                <li key={i.id}>
                  <LigneChoix
                    issue={i}
                    maintenant={maintenant}
                    delai={Math.min(rang++, 12) * 0.025}
                    onClick={() => vers(lienProbleme(i.id), { ouvrirNcs: true })}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}

function LigneChoix({ issue, maintenant, delai, onClick }: { issue: Issue; maintenant: number; delai: number; onClick: () => void }): JSX.Element {
  return (
    <button type="button" className="declarer-ligne" style={{ animationDelay: `${delai}s` }} onClick={onClick}>
      <span className="declarer-machine">{issue.numeroSerie || '—'}</span>
      <span className="declarer-ligne-texte">
        <span className="declarer-ligne-titre">{issue.titre}</span>
        <span className="declarer-ligne-meta">
          <PastilleNcs issue={issue} />
          {[issue.id, ageCourt(issue.dateDeclaration, maintenant)].filter(Boolean).join(' · ')}
        </span>
      </span>
      <EtatProbleme statut={issue.statut} criticite={issue.criticite} />
    </button>
  )
}
